import { createSelfHostedCameraTransport } from './selfHostedCameraTransport';
// Read-only surface. No publisher, permission prompt, Start or delete capability.
export function createSharedCameraStreams({ api, isCurrent = () => true, urls = URL, mediaOrigin, transportFactory = createSelfHostedCameraTransport }) {
  const initial = view => ({ open: false, view, sessions: [], loaded: false, loading: false, nextCursor: null,
    exhausted: false, error: null, retryMore: false, selectedId: null, addressedId: null, parts: [], partName: null, url: null, stream: null, mediaStatus: 'idle' });
  let state = initial('live'), disposed = false, pageRequest = null, mediaRequest = null, revision = 0;
  const listeners = new Set(), videos = new Map();
  let liveTransport = null, unsubscribeLive = null, expiryTimer = null;
  const clearVideo = video => { try { video.pause(); video.srcObject = null; video.removeAttribute('src'); video.load(); } catch { /* Detached browser element. */ } };
  const current = () => !disposed && isCurrent();
  const emit = patch => { state = { ...state, ...patch }; if (current()) listeners.forEach(fn => fn(state)); };
  function release() {
    revision++; mediaRequest?.abort(); mediaRequest = null;
    clearTimeout(expiryTimer); expiryTimer = null;
    unsubscribeLive?.(); unsubscribeLive = null;
    videos.forEach((_, video) => clearVideo(video)); videos.clear();
    const transport = liveTransport; liveTransport = null;
    // dispose closes the peer/tracks synchronously; remote DELETE stays bounded
    // and uncertain. Its late receipt never mutates this selection or a successor.
    if (transport) void transport.dispose().catch(() => {});
    const url = state.url;
    state = { ...state, selectedId: null, parts: [], partName: null, url: null, stream: null, mediaStatus: 'idle' };
    if (url) urls.revokeObjectURL(url);
  }
  function reset(view = state.view) {
    pageRequest?.abort(); pageRequest = null; release(); state = initial(view);
  }
  function close() { reset(); emit({}); }
  async function load(more = false) {
    if (!current() || !state.open || pageRequest || (more && !state.nextCursor)) return;
    if (!more) { release(); emit({ sessions: [], loaded: false, nextCursor: null, exhausted: false }); }
    const request = new AbortController(), view = state.view, cursor = more ? state.nextCursor : null;
    pageRequest = request; emit({ loading: true, error: null, retryMore: more });
    const allowed = () => current() && state.open && pageRequest === request && !request.signal.aborted;
    try {
      const page = await api.list(view, cursor, request.signal);
      if (!allowed()) return;
      const rows = new Map(state.sessions.map(row => [row.id, row]));
      page.sessions.forEach(row => rows.set(row.id, row));
      emit({ sessions: [...rows.values()], loaded: true, nextCursor: page.nextCursor, exhausted: page.exhausted });
    } catch {
      if (allowed()) emit({ error: 'Streams could not be loaded. Retry or refresh.' });
    } finally { if (pageRequest === request) { pageRequest = null; emit({ loading: false }); } }
  }
  async function select(id) {
    if (!current() || !state.open) return;
    const row = state.sessions.find(s => s.id === id);
    const addressed = state.view === 'live' && state.addressedId === id;
    if (!row && !addressed) return;
    release(); emit({ selectedId: id, mediaStatus: 'loading' });
    const request = new AbortController(), token = revision; mediaRequest = request;
    const allowed = () => current() && state.open && revision === token && !request.signal.aborted;
    try {
      // Always ask current media authority, never turn discovery into a grant.
      if (state.view === 'live') {
        if (!addressed && row.state !== 'live') throw new Error('Not live');
        const grant = await api.live(id, request.signal);
        if (!allowed()) return;
        api.assertCurrent();
        const remaining = Math.min(grant.expiresAt - Date.now(), 2147483647);
        if (!(remaining > 0)) throw new Error('Expired');
        const deadline = performance.now() + remaining;
        const fail = () => { if (allowed()) { release(); emit({ selectedId: id, mediaStatus: 'unavailable' }); } };
        const fresh = () => allowed() && Date.now() < grant.expiresAt && performance.now() < deadline;
        expiryTimer = setTimeout(fail, remaining);
        const transport = transportFactory({ role: 'view', authorizedMediaOrigin: mediaOrigin });
        liveTransport = transport;
        let negotiated = false;
        const update = value => {
          if (!allowed()) return;
          if (!fresh() || ['error', 'stopped'].includes(value.status)) { fail(); return; }
          const hasVideo = value.stream?.getVideoTracks().some(track => track.readyState === 'live');
          emit({ stream: value.stream, mediaStatus: negotiated && value.status === 'connected' && hasVideo ? 'connected' : 'connecting' });
        };
        unsubscribeLive = transport.subscribe(update);
        emit({ mediaStatus: 'connecting' });
        await transport.start({ endpoint: grant.endpoint, token: grant.token });
        if (!allowed()) return;
        negotiated = true; update(transport.getState());
        return;
      }
      const parts = await api.parts(id, request.signal);
      if (allowed()) emit({ parts, mediaStatus: parts.length ? 'choose' : 'unavailable' });
    } catch { if (allowed()) { release(); emit({ selectedId: id, parts: [], mediaStatus: 'unavailable' }); } }
  }
  async function play(name) {
    const id = state.selectedId, part = state.parts.find(p => p.name === name);
    if (!current() || !state.open || state.view !== 'history' || !part) return;
    const parts = state.parts; release(); emit({ selectedId: id, parts, partName: name, mediaStatus: 'loading' });
    const request = new AbortController(), token = revision; mediaRequest = request;
    const allowed = () => current() && state.open && revision === token && !request.signal.aborted;
    try {
      const blob = await api.media(id, part, request.signal);
      if (!allowed()) return;
      api.assertCurrent(); const url = urls.createObjectURL(blob);
      if (!allowed()) { urls.revokeObjectURL(url); return; }
      emit({ url, mediaStatus: 'ready' });
    } catch { if (allowed()) emit({ parts: [], partName: null, mediaStatus: 'unavailable' }); }
  }
  return { getState: () => state, subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    open() { if (!current() || state.open) return; emit({ open: true }); void load(); }, close, load, select, play,
    // A Media card names the target. Discovery position is not permission:
    // check that exact ID through the bounded current viewer-token authority.
    async watch(id) {
      if (!current() || typeof id !== 'string' || !/^[a-f0-9]{24}$/.test(id)) return;
      reset('live'); emit({ open: true, addressedId: id });
      await select(id);
    },
    tab(view) { if (!current() || !state.open || !['live','history'].includes(view) || view === state.view) return; reset(view); emit({ open: true }); void load(); },
    attachVideo(video, stream) {
      if (!current() || !stream || state.stream !== stream) return () => {};
      videos.set(video, stream); video.srcObject = stream;
      return () => { if (videos.get(video) === stream) { videos.delete(video); clearVideo(video); } };
    },
    clearSelection() { release(); emit({}); },
    playbackError(expected = state.stream || state.url) {
      if (!current() || !expected || (state.stream !== expected && state.url !== expected)) return;
      const id = state.selectedId; release(); emit({ selectedId: id, mediaStatus: 'unavailable' });
    },
    dispose() { disposed = true; close(); listeners.clear(); },
  };
}
